import { apiRoute, API_V1 } from '@/helpers/api-route'
import { fetchWrapper } from '@/helpers/fetch-wrapper'

const API_BASE = apiRoute() + API_V1 + '/training'

export const annotationService = {
  /**
   * Get next unlabeled image for annotation
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Next image object
   */
  async getNextImage (projectId) {
    try {
      const response = await fetchWrapper.get(`${API_BASE}/images/next?projectId=${projectId}`)
      return response
    } catch (error) {
      console.error('Get next image error:', error)
      throw error
    }
  },

  /**
   * Get specific image by ID with label restoration data
   * @param {string} imageId - Image ID
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Image object with labels if previously annotated
   */
  async getSpecificImage (imageId, projectId) {
    try {
      const response = await fetchWrapper.get(`${API_BASE}/images/next-specific?imageId=${imageId}&projectId=${projectId}`)
      return response
    } catch (error) {
      console.error('Get specific image error:', error)
      throw error
    }
  },

  /**
   * Get count of unlabeled images
   * @param {string} projectId - Project ID
   * @returns {Promise<number>} Count of unlabeled images
   */
  async getImageCount (projectId) {
    try {
      const response = await fetchWrapper.get(`${API_BASE}/images/count?projectId=${projectId}`)
      return response.count || 0
    } catch (error) {
      console.error('Get image count error:', error)
      return 0
    }
  },

  /**
   * Get specific image by ID
   * @param {string} imageId - Image ID
   * @returns {Promise<Object>} Image object
   */
  async getImage (imageId) {
    try {
      const response = await fetchWrapper.get(`${API_BASE}/images/${imageId}`)
      return response
    } catch (error) {
      console.error('Get image error:', error)
      throw error
    }
  },

  /**
   * Save label data for an image
   * @param {string} imageId - Image ID
   * @param {Object} labelData - Label data
   * @returns {Promise<Object>} Saved label object
   */
  async saveLabel (imageId, labelData) {
    try {
      const response = await fetchWrapper.post(`${API_BASE}/labels/${imageId}`, labelData)
      return response || { success: true }
    } catch (error) {
      console.error('Save label error:', error)
      throw error
    }
  },

  /**
   * Get AI-generated labels for an image
   * @param {string} imageId - Image ID
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} Array of AI-generated pathologies
   */
  async getAILabels (imageId, projectId) {
    try {
      const response = await fetchWrapper.post(`${API_BASE}/labels/${imageId}/ai`, { projectId })
      return response
    } catch (error) {
      console.error('Get AI labels error:', error)
      throw error
    }
  },

  /**
   * Get labeled images for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} Array of labeled images
   */
  async getLabeledImages (projectId) {
    try {
      const response = await fetchWrapper.get(`${API_BASE}/labels?projectId=${projectId}`)
      return response
    } catch (error) {
      console.error('Get labeled images error:', error)
      // Return empty array for development
      return []
    }
  },

  /**
   * Get project configuration (pathologies, diagnoses, tags)
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Project configuration
   */
  async getProjectConfig (projectId) {
    try {
      const response = await fetchWrapper.get(`${API_BASE}/images/projects/${projectId}/diagnosis-tags-pathologies`)
      return response
    } catch (error) {
      console.error('Get project config error:', error)
      // Return mock data for development
      return {
        pathologies: [
          { value: 'consolidation', label: 'Consolidation', color: '#ed1a4f' },
          { value: 'pneumothorax', label: 'Pneumothorax', color: '#0066cc' },
          { value: 'pleural_effusion', label: 'Pleural Effusion', color: '#00cc66' }
        ],
        diagnosis: [
          { value: 'normal', label: 'Normal' },
          { value: 'pneumonia', label: 'Pneumonia' },
          { value: 'covid19', label: 'COVID-19' }
        ],
        tags: ['urgent', 'follow_up', 'reviewed'],
        freehand_enabled: true,
        ai_enabled: true
      }
    }
  }
}
